import * as React from 'react';
import AnimateHeight from 'react-animate-height';
import { Field, AnyObject } from 'react-final-form';
import { SelectItem } from 'src/models/common/SelectItem';

interface ITagSelectProps {
    name: string;
    isDisabled?: boolean;
    className?: string;
    items: SelectItem[];
    tabIndex?: number;
    onSelect?: () => void;
    onTabKey?: (id: number) => void;
    blur?: () => void;
    inputRef: React.RefObject<HTMLInputElement>;
}

interface ITagSelectState {
    selectListShowing: boolean;
    arrowDirty: boolean;
    searchTerm: string;
    currentIndex: number;
    filteredList: Array<SelectItem>;
}

class TagSelect extends React.Component<ITagSelectProps, ITagSelectState> {
    constructor(props: ITagSelectProps) {
        super(props);
        this.state = {
            selectListShowing: false,
            arrowDirty: false,
            searchTerm: '',
            currentIndex: 0,
            filteredList: this.props.items,
        };
    }

    keydownHandler = (e: React.KeyboardEvent<HTMLInputElement>, input: AnyObject) => {
        // auto-complete list popup
        if (e.key === 'ArrowDown') {
            const filteredList = this.state.filteredList ? this.state.filteredList : this.props.items;
            this.setState({
                selectListShowing: true,
                filteredList: filteredList,
                currentIndex: Math.min(this.state.selectListShowing ? this.state.currentIndex + 1 : 0, filteredList.length - 1),
                arrowDirty: true,
            });
        } else if (e.key === 'ArrowUp') {
            this.setState({
                currentIndex: Math.max(this.state.currentIndex - 1, 0),
                arrowDirty: true,
            });
            // prevent form submit and select item highlighted
        } else if (e.key === 'Tab') {
            e.preventDefault();
            // if the length of filteredList is zero there are guaranteed no matches!
            if (this.state.filteredList.length > 0) {
                e.stopPropagation();

                // indicates that the user used the arrow keys for selection before hitting enter
                if (this.state.arrowDirty) {
                    const selectItem = this.state.filteredList[this.state.currentIndex];

                    input.onChange(selectItem.id);
                    if (this.props.onSelect) {
                        this.props.onSelect();
                    }
                    // matches both exact match and incomplete match
                } else {
                    const selectItem = this.state.filteredList[this.state.currentIndex];
                    input.onChange(selectItem.id);
                    if (this.props.onTabKey) {
                        this.props.onTabKey(selectItem.id as number);
                    }
                }
                // if the length of filteredList is zero there are guaranteed no matches!
            } else {
                input.onChange(undefined);
                if (this.props.onTabKey) {
                    this.props.onTabKey(-1);
                }
            }
            this.setState({
                selectListShowing: false,
                searchTerm: '',
                currentIndex: 0,
                arrowDirty: false,
            });

            // Tab to next item without selecting
        } else if (e.key === 'Tab') {
            if (this.state.filteredList.length > 0) {
                if (this.state.selectListShowing) {
                    e.preventDefault();
                }
                if (this.state.selectListShowing) {
                    const selectItem = this.state.filteredList[this.state.currentIndex];
                    this.setState({
                        selectListShowing: false,
                        searchTerm: '',
                    });
                    input.onChange(selectItem.id);
                    if (this.props.onSelect) {
                        this.props.onSelect();
                    }
                }
            }
            // remove selected item from selection and show select list
        } else if (e.key === 'Delete') {
            this.setState({
                searchTerm: undefined,
                currentIndex: 0,
                selectListShowing: true,
                filteredList: this.props.items,
            });
            input.onChange(undefined);
            if (this.props.onSelect) {
                this.props.onSelect();
            }
            // if typing in empty select box, show select list
        } else if (/^[a-z\d\-_\s]$/i.test(e.key) || !this.state.selectListShowing) {
            if (!e.shiftKey) {
                this.setState({
                    selectListShowing: true,
                    currentIndex: 0,
                });
            }
            input.onChange(undefined);
        }
    };

    blur = (input: AnyObject) => {
        setTimeout(() => {
            this.setState({
                selectListShowing: false,
                searchTerm: '',
                filteredList: this.props.items,
                arrowDirty: false,
            });
        }, 0);
        this.props.blur();
    };

    hoverItem = (index: number) => {
        this.setState({ currentIndex: index });
    };

    change = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (this.props.items) {
            const filteredItems = this.props.items.filter(
                (item: SelectItem) => item.name.toLowerCase().indexOf(this.props.inputRef.current.value.toLowerCase()) >= 0
            );

            this.setState({
                filteredList: filteredItems,
                searchTerm: e.target.value,
            });
        }
    };

    itemClick = (item: SelectItem, input: AnyObject) => {
        this.setState({ searchTerm: '', selectListShowing: false });
        input.onChange(item.id);
        if (this.props.onSelect) {
            this.props.onSelect();
        }
    };

    filteredSelectList = (input: AnyObject) => {
        if (!this.state.filteredList || this.state.filteredList.length <= 0) {
            return (
                <>
                    <div className="lh-align-center">No Tags Found</div>
                </>
            );
        }
        return (
            <>
                {this.state.filteredList.map((item: SelectItem, index: number) => {
                    return (
                        <div
                            key={item.id}
                            className={`select-item ${this.state.currentIndex === index ? 'hover' : ''}`}
                            onMouseEnter={() => this.hoverItem(index)}
                            onClick={() => this.itemClick(item, input)}
                        >
                            {item.name}
                        </div>
                    );
                })}
            </>
        );
    };

    showDropdown = () => {
        if (!this.state.selectListShowing) {
            this.setState({ selectListShowing: true, filteredList: this.props.items });
        }
    };

    mouseDownHandler = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        this.props.inputRef.current.focus();
    };
    render() {
        return (
            <Field name={this.props.name} key={this.props.name}>
                {({ input, meta }) => {
                    const hasError = meta.submitFailed && meta.error;
                    const dropDownShowing = this.state.selectListShowing;

                    const inputClass = hasError ? 'error ' : '';
                    const errorHeight = hasError ? 'auto' : 0;

                    let item = undefined;
                    if (this.props.items) {
                        item = this.props.items.filter((i: SelectItem) => i.id === input.value)[0];
                    }

                    return (
                        <div className={`${this.props.className}`} onMouseDown={this.mouseDownHandler}>
                            <div className={`dlc-tag-select ${inputClass}`}>
                                <input
                                    style={{ height: '3rem' }}
                                    ref={this.props.inputRef}
                                    tabIndex={this.props.tabIndex}
                                    className={`dlc-tag-select-input ${inputClass}`}
                                    value={this.state.searchTerm || ''}
                                    onChange={this.change}
                                    onBlur={this.blur}
                                    onClick={this.showDropdown}
                                    disabled={this.props.isDisabled}
                                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => this.keydownHandler(e, input)}
                                />
                                {item && <div className="dlc-tag-select-selected-item">{item.name}</div>}

                                <div className={`dlc-tag-select-dropdown ${dropDownShowing ? 'showing' : 0}`}>
                                    <AnimateHeight height={this.state.selectListShowing ? 'auto' : 0}>
                                        {this.filteredSelectList(input) || <></>}
                                    </AnimateHeight>
                                </div>
                            </div>

                            <AnimateHeight height={errorHeight} duration={300}>
                                <div className="dlc-form-textbox-error">{meta.error}</div>
                            </AnimateHeight>
                        </div>
                    );
                }}
            </Field>
        );
    }
}

export default React.forwardRef((props: ITagSelectProps, inputRef: React.RefObject<HTMLInputElement>) => (
    <TagSelect inputRef={inputRef} {...props} />
));



// WEBPACK FOOTER //
// ./src/components/controls/forms/redesign/TagSelect.tsx